import { ROLE_LABELS, roleSchema, type ListUsersQuery, type Role } from '@idpe/schema';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import {
  type SortState,
  DataTable,
  PaginationControls,
  useDataTableSort,
} from '~/components/ui/DataTable.js';
import { Field } from '~/components/ui/Field.js';
import { Select } from '~/components/ui/Select.js';
import { UserCreateDialog } from '~/features/users/UserCreateDialog.js';
import { useUsersList } from '~/features/users/hooks.js';

const searchSchema = z.object({
  q: z.string().optional(),
  papel: roleSchema.optional(),
  ativo: z
    .string()
    .optional()
    .transform((v) => (v === 'true' ? true : v === 'false' ? false : undefined)),
  page: z.coerce.number().int().min(1).default(1).optional(),
  perPage: z.coerce.number().int().min(1).max(100).default(20).optional(),
  sort: z.string().optional(),
});

const ROLE_TONE: Record<Role, 'primary' | 'success' | 'warning' | 'neutral'> = {
  admin: 'primary',
  publisher: 'success',
  revisor: 'warning',
  editor: 'neutral',
};

export const Route = createFileRoute('/_auth/users/')({
  validateSearch: (search) => searchSchema.parse(search),
  component: UsersListPage,
});

function UsersListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [creating, setCreating] = useState(false);
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [sort, setSort] = useDataTableSort(
    search.sort
      ? (() => {
          const [campo, dir] = search.sort.split(':');
          return campo
            ? ({ campo, dir: (dir === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc' } as SortState)
            : undefined;
        })()
      : undefined,
  );

  const query: Partial<ListUsersQuery> = {
    q: search.q,
    papel: search.papel,
    ativo: search.ativo,
    page: search.page ?? 1,
    perPage: search.perPage ?? 20,
    sort: sort ? `${sort.campo}:${sort.dir}` : undefined,
  };

  const { data, isLoading, error, refetch } = useUsersList(query);

  function updateSearch(patch: Partial<typeof search>) {
    navigate({
      to: '/users',
      search: { ...search, ...patch, page: patch.page ?? 1 },
    });
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Configurações</p>
        <h1 className="page-title">Usuários</h1>
        <p className="page-sub">
          Gerencie quem tem acesso ao painel. Apenas administradores podem criar, editar papéis ou
          desativar usuários.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <div className="toolbar__filters">
              <Field
                label="Buscar"
                placeholder="nome ou e-mail"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateSearch({ q: searchInput || undefined });
                }}
                onBlur={() => updateSearch({ q: searchInput || undefined })}
              />
              <Select
                label="Papel"
                placeholder="todos"
                value={search.papel ?? ''}
                onChange={(e) => updateSearch({ papel: (e.target.value as Role) || undefined })}
                options={[
                  { value: '', label: 'Todos os papéis' },
                  ...Object.entries(ROLE_LABELS).map(([v, l]) => ({ value: v, label: l })),
                ]}
              />
              <Select
                label="Status"
                value={typeof search.ativo === 'boolean' ? String(search.ativo) : ''}
                onChange={(e) =>
                  updateSearch({
                    ativo:
                      e.target.value === 'true'
                        ? true
                        : e.target.value === 'false'
                          ? false
                          : undefined,
                  })
                }
                options={[
                  { value: '', label: 'Todos' },
                  { value: 'true', label: 'Ativos' },
                  { value: 'false', label: 'Desativados' },
                ]}
              />
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="primary" onClick={() => setCreating(true)} fullWidth>
                  Novo usuário
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 'var(--s-5)' }}>
          <DataTable
            data={data?.data ?? []}
            loading={isLoading}
            error={
              error
                ? {
                    message: error.displayMessage,
                    retry: () => void refetch(),
                  }
                : null
            }
            keyExtractor={(u) => u.id}
            sort={sort}
            onSortChange={(s) => {
              setSort(s);
              updateSearch({ sort: s ? `${s.campo}:${s.dir}` : undefined });
            }}
            onRowClick={(u) => navigate({ to: '/users/$id', params: { id: u.id } })}
            emptyMessage="Nenhum usuário encontrado com esses filtros."
            emptyAction={
              <Button variant="secondary" onClick={() => setCreating(true)}>
                Criar primeiro usuário
              </Button>
            }
            columns={[
              {
                key: 'nome',
                header: 'Nome',
                sortable: true,
                mobileLine: 'primary',
                cell: (u) => <strong>{u.nome}</strong>,
              },
              {
                key: 'email',
                header: 'E-mail',
                sortable: true,
                mobileLine: 'secondary',
                cell: (u) => <span style={{ color: 'var(--fg-muted)' }}>{u.email}</span>,
              },
              {
                key: 'papel',
                header: 'Papel',
                mobileLine: 'meta',
                cell: (u) => <Badge tone={ROLE_TONE[u.papel]}>{ROLE_LABELS[u.papel]}</Badge>,
              },
              {
                key: 'ativo',
                header: 'Status',
                mobileLine: 'meta',
                cell: (u) =>
                  u.ativo ? (
                    <Badge tone="success">ativo</Badge>
                  ) : (
                    <Badge tone="muted">desativado</Badge>
                  ),
              },
              {
                key: 'ultimoLogin',
                header: 'Último login',
                sortable: true,
                mobileLine: 'meta',
                cell: (u) =>
                  u.ultimoLogin
                    ? new Date(u.ultimoLogin).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—',
              },
            ]}
          />
        </div>

        {data && data.total > 0 && (
          <PaginationControls
            page={data.page}
            perPage={data.perPage}
            total={data.total}
            totalPages={data.totalPages}
            onPageChange={(page) => updateSearch({ page })}
          />
        )}
      </section>

      <UserCreateDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}
